import React from "react";
import { Chip } from "@mui/material";
import "./profile.css";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
} from "reactstrap";
import { TbAlertTriangleFilled } from "react-icons/tb";
import { FaRegEye, FaClock, FaChartBar, FaTrophy } from "react-icons/fa";
import { IoTrophy, IoStatsChart } from "react-icons/io5";
import { FaRankingStar } from "react-icons/fa6";
import { BiSolidMedal } from "react-icons/bi";
import { MdDashboard, MdTrendingUp } from "react-icons/md";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import useApiRequests from "../../../hooks/useApiRequests";
import { useNotification } from "../../../hooks/useNotification";
import { Spin } from "react-cssfx-loading";
import ComponentTitle from "../../Text/Title/Title";
import {useTheme} from '../../../Context/ThemeContext';
import BigText from "../../Text/BigText/BigText";
import TitleComponent from "../../Text/Title/Title.jsx";
// import { SubTitle } from "../../Text/Subtitle/SubTitle.tsx";


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "top" as const,
    },
  },
};

//#region Interfaces

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
  }[];
}
interface Metrics {
  Id: string | null;
  MemberId: string;
  TotalTasks: number;
  TotalCompletedTasks: number;
  AverageResolutionTime: string;
  Rating: number;
}

interface Member {
  id: string;
  trelloId: string;
  aaId: string;
  fullName: string;
}

interface MetricsData {
  id: string | null;
  Metrics: Metrics;
  Member: Member;
}

interface TicketData {
  id: {
    timestamp: number;
    machine: number;
    pid: number;
    increment: number;
    creationTime: string;
  };
  title: string;
  category: string;
  ticket: string;
  partnerEmail: string;
  screenerId: string | null;
  screenerName: string | null;
  partnersEmailsList: string[];
  partnersCustomers: any[];
  customerEmail: string;
  customersEmailsList: string[];
  description: string;
  shortUrl: string;
  listId: string;
  listName: string;
  created: string;
  createdBy: string | null;
  creatorName: string | null;
  originalDescription: string | null;
  testerId: string | null;
  testerName: string | null;
  trelloId: string;
  devId: string[];
  devName: string[];
  labelIds: string[];
  emailsToNotify: string[] | null;
  resources: any[] | null;
  memberIds: string[];
  membersName: string[];
  toolsIds: string[];
  toolsName: string[];
  priority: string;
  complexity: string;
  finishDate: string;
  version: string | null;
  evidences: string | null;
  flow: string;
  fromApi: string | null;
  isApproved: boolean;
  approveDate: string;
  observations: string | null;
}

//#endregion Interface

// Função para gerar números aleatórios dentro de um intervalo
function getRandomNumber(min: any, max: any) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
const Profile = () => {
  const {
    getMetrics,
    getDeadlineTasks,
    getCurrentMember,
    getComplexityGraph,
    getToolsGraph,
  } = useApiRequests();
  const { notify } = useNotification();
  const { theme, toggleTheme } = useTheme();


  //#region States

  const [customDateRange, setCustomDateRange] = React.useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const [sevenDaysMetrics, setSevenDaysMetrics] =
    React.useState<MetricsData | null>(null);
  const [deadlineTasks, setDeadLineTasks] = React.useState<TicketData[] | null>(
    null
  );

  const [currentMember, setCurrentMember] = React.useState<Member | null>(null);
  const [rankingIndex, setRankingIndex] = React.useState<string>("");
  const [complexityGraphData, setComplexityGraphData] =
    React.useState<ChartData | null>(null);

  const [toolsGraphData, setToolsGraphData] = React.useState<ChartData | null>(
    null
  );
  //#endregion States

  //#region UseEffects

  React.useEffect(() => {
    getLastSevenDaysMetrics();
    getDeadline();
    getAlluserMetrics();
    getMember();
    getComplexityTasksGraph();
    getToolsTaskGraph();
  }, []);

  //#endregion UseEffects

  //#region Logic

  function sortByRating(members: any) {
    members.sort((a: any, b: any) => {
      const ratingA = a.Metrics.Rating || 0;
      const ratingB = b.Metrics.Rating || 0;

      return ratingB - ratingA;
    });
    return members;
  }

  function findCurrentUserIndex(sortedMembers: any, currentUserId: any) {
    for (let i = 0; i < sortedMembers.length; i++) {
      if (sortedMembers[i].Member.trelloId === currentUserId) {
        return i;
      }
    }
    return -1; // Retorna -1 se o usuário atual não for encontrado
  }

  async function getToolsTaskGraph() {
    const data = await getToolsGraph();
    if (data.success) {
      const labels = Object.keys(data.data);

      const graphData = {
        labels,
        datasets: [
          {
            label: "Tracker",
            data: labels.map((label) => data.data[label] || 0),
            backgroundColor: "rgba(255, 99, 132, 0.5)",
          },
        ],
      };

      setToolsGraphData(graphData);
    } else {
      notify("Ocorreu um erro ao buscar os dados do gráfico!", {
        type: "error",
      });
    }
  }

  async function getComplexityTasksGraph() {
    const data = await getComplexityGraph();
    if (data.success) {
      const labels = Object.keys(data.data);

      const graphData = {
        labels,
        datasets: [
          {
            label: "Tracker",
            data: labels.map((label) => data.data[label] || 0),
            backgroundColor: "rgba(255, 99, 132, 0.5)",
          },
        ],
      };

      setComplexityGraphData(graphData);
    } else {
      notify("Ocorreu um erro ao buscar os dados do gráfico!", {
        type: "error",
      });
    }
  }

  async function getMember() {
    const data = await getCurrentMember();
    setCurrentMember(data.data);
  }

  async function getAlluserMetrics() {
    const data = await getMetrics("Últimos 7 dias", customDateRange, "all");
    if (data.msg != null && data.msg != "") {
      notify(data.msg, { type: "alert" });
    } else {
      const result = sortByRating(JSON.parse(data.data));
      const memberData = await getCurrentMember();
      setCurrentMember(memberData.data);
      const index = findCurrentUserIndex(result, memberData.data.trelloId);
      setRankingIndex((index + 1).toString());
    }
  }

  async function getDeadline() {
    const data = await getDeadlineTasks();
    if (data.msg != null && data.msg != "") {
      notify(data.msg, { type: "alert" });
    } else {
      setDeadLineTasks(data.data);
    }
  }

  async function getLastSevenDaysMetrics() {
    const data = await getMetrics("Últimos 7 dias", customDateRange, "true");
    if (data.msg != null && data.msg != "") {
      notify(data.msg, { type: "alert" });
    } else {
      setSevenDaysMetrics(JSON.parse(data.data)[0]);
    }
  }

  const formatDate = (dateString: string) => {
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    const formattedDate = new Date(dateString).toLocaleDateString(
      "pt-BR",
      options
    );
    return formattedDate;
  };

  const calculateDateDifference = (finishDate: string) => {
    const currentDate = new Date();
    const estimatedDate = new Date(finishDate);
    const differenceInDays = Math.floor(
      (estimatedDate - currentDate) / (24 * 60 * 60 * 1000)
    );

    if (differenceInDays < 0) {
      return "urgent";
    } else if (differenceInDays <= 2) {
      return "close";
    } else if (differenceInDays <= 4) {
      return "low";
    } else {
      return "nothing"; // Adicione sua classe normal aqui
    }
  };


  //#endregion Logic

  const handleGoBack = () => {
    const lastHref = localStorage.getItem("lastHref");
    window.location.href = lastHref || "";
  };


  return (
    <div className="profile-container">
      {/* Header Section */}
      <div className="profile-header">
        <div className="profile-header-content">
          <div className="profile-user-info">
            <div className="profile-avatar">
              {currentMember?.fullName ? currentMember.fullName.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="profile-user-details">
              <h1 className="profile-username">
                {currentMember?.fullName || "Carregando..."}
              </h1>
              <p className="profile-subtitle">Dashboard Pessoal</p>
            </div>
          </div>
          <Button
            className="profile-back-btn"
            color="secondary"
            onClick={() => handleGoBack()}
          >
            Voltar
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="profile-main-content">
        {/* Top Section - Deadlines and Metrics */}
        <div className="profile-section">
          <Row className="profile-cards-row">
            <Col md={8}>
              <div className="profile-card profile-card-primary">
                <div className="profile-card-header">
                  <div className="profile-card-header-content">
                    <div className="profile-card-icon bg-blue">
                      <TbAlertTriangleFilled size={20} />
                    </div>
                    <div>
                      <h3 className="profile-card-title">Tarefas por Prazo</h3>
                      <p className="profile-card-subtitle">Acompanhe seus prazos</p>
                    </div>
                  </div>
                </div>
                <div className="profile-card-body">
                  {deadlineTasks == null ? (
                    <div className="profile-loading">
                      <Spin />
                    </div>
                  ) : deadlineTasks.length === 0 ? (
                    <div className="profile-empty-state">
                      <FaClock size={48} />
                      <p>Nenhuma tarefa com prazo definido</p>
                    </div>
                  ) : (
                    <div className="profile-tasks-list">
                      {deadlineTasks.slice(0, 5).map((task, index) => (
                        <div
                          key={index}
                          className={`profile-task-item ${calculateDateDifference(task.finishDate)}`}
                        >
                          <div className="profile-task-content">
                            <h5 className="profile-task-title">
                              {task.title.length > 60
                                ? `${task.title.slice(0, 60)}...`
                                : task.title}
                            </h5>
                            <div className="profile-task-meta">
                              <span className="profile-task-date">
                                <FaClock size={12} />
                                Estimativa: {formatDate(task.finishDate)}
                              </span>
                            </div>
                          </div>
                          <div className="profile-task-actions">
                            <button
                              className="profile-task-view-btn"
                              onClick={() =>
                                window.open(
                                  `https://devtracker.vendaerp.com.br/task/view/${task.trelloId}`,
                                  "_blank"
                                )
                              }
                            >
                              <FaRegEye size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="profile-card profile-card-warning">
                <div className="profile-card-header">
                  <div className="profile-card-header-content">
                    <div className="profile-card-icon bg-orange">
                      <IoTrophy size={20} />
                    </div>
                    <div>
                      <h3 className="profile-card-title">Minhas Métricas</h3>
                      <p className="profile-card-subtitle">Últimos 7 dias</p>
                    </div>
                  </div>
                </div>
                <div className="profile-card-body">
                  {sevenDaysMetrics == null ? (
                    <div className="profile-loading">
                      <Spin />
                    </div>
                  ) : (
                    <div className="profile-metrics">
                      <div className="profile-metric-item">
                        <div className="profile-metric-number">
                          {sevenDaysMetrics.Metrics.TotalCompletedTasks}
                          <span className="profile-metric-total">
                            /{sevenDaysMetrics.Metrics.TotalTasks}
                          </span>
                        </div>
                        <div className="profile-metric-label">Tarefas Concluídas</div>
                      </div>
                      <div className="profile-metric-divider"></div>
                      <div className="profile-metric-item">
                        <div className="profile-metric-number">
                          {sevenDaysMetrics.Metrics.Rating.toFixed(1)}%
                        </div>
                        <div className="profile-metric-label">Taxa de Resolução</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Bottom Section - Charts and Ranking */}
        <div className="profile-section">
          <Row className="profile-cards-row">
            <Col lg={6} xl={4}>
              <div className="profile-card profile-card-success">
                <div className="profile-card-header">
                  <div className="profile-card-header-content">
                    <div className="profile-card-icon bg-green">
                      <FaTrophy size={20} />
                    </div>
                    <div>
                      <h3 className="profile-card-title">Ranking</h3>
                      <p className="profile-card-subtitle">Últimos 7 dias</p>
                    </div>
                  </div>
                </div>
                <div className="profile-card-body">
                  {rankingIndex === "" ? (
                    <div className="profile-loading">
                      <Spin width={60} height={60} />
                    </div>
                  ) : (
                    <div className="profile-ranking">
                      <div className="profile-ranking-icon">
                        <FaRankingStar size={48} />
                      </div>
                      <div className="profile-ranking-text">
                        <span className="profile-ranking-position">{rankingIndex}º</span>
                        <span className="profile-ranking-label">Lugar</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={6} xl={4}>
              <div className="profile-card profile-card-info">
                <div className="profile-card-header">
                  <div className="profile-card-header-content">
                    <div className="profile-card-icon bg-blue">
                      <FaChartBar size={20} />
                    </div>
                    <div>
                      <h3 className="profile-card-title">Complexidade</h3>
                      <p className="profile-card-subtitle">Distribuição de tarefas</p>
                    </div>
                  </div>
                </div>
                <div className="profile-card-body profile-chart-container">
                  {complexityGraphData == null ? (
                    <div className="profile-loading">
                      <Spin width={60} height={60} />
                    </div>
                  ) : (
                    <>
                      <div className="profile-chart">
                        <Bar 
                          options={{
                            ...options,
                            plugins: {
                              ...options.plugins,
                              legend: {
                                display: false
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                grid: {
                                  color: 'rgba(255, 255, 255, 0.1)'
                                }
                              },
                              x: {
                                grid: {
                                  display: false
                                }
                              }
                            }
                          }} 
                          data={{
                            ...complexityGraphData,
                            datasets: [{
                              ...complexityGraphData.datasets[0],
                              backgroundColor: 'rgba(66, 133, 244, 0.8)',
                              borderColor: 'rgba(66, 133, 244, 1)',
                              borderWidth: 2,
                              borderRadius: 4
                            }]
                          }} 
                        />
                      </div>
                      <p className="profile-chart-note">
                        Apenas tarefas com complexidade informada
                      </p>
                    </>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={12} xl={4}>
              <div className="profile-card profile-card-secondary">
                <div className="profile-card-header">
                  <div className="profile-card-header-content">
                    <div className="profile-card-icon bg-teal">
                      <MdTrendingUp size={20} />
                    </div>
                    <div>
                      <h3 className="profile-card-title">Ferramentas</h3>
                      <p className="profile-card-subtitle">Tarefas por ferramenta</p>
                    </div>
                  </div>
                </div>
                <div className="profile-card-body profile-chart-container">
                  {toolsGraphData == null ? (
                    <div className="profile-loading">
                      <Spin width={60} height={60} />
                    </div>
                  ) : (
                    <>
                      <div className="profile-chart">
                        <Bar 
                          options={{
                            ...options,
                            plugins: {
                              ...options.plugins,
                              legend: {
                                display: false
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                grid: {
                                  color: 'rgba(255, 255, 255, 0.1)'
                                }
                              },
                              x: {
                                grid: {
                                  display: false
                                }
                              }
                            }
                          }} 
                          data={{
                            ...toolsGraphData,
                            datasets: [{
                              ...toolsGraphData.datasets[0],
                              backgroundColor: 'rgba(26, 188, 156, 0.8)',
                              borderColor: 'rgba(26, 188, 156, 1)',
                              borderWidth: 2,
                              borderRadius: 4
                            }]
                          }} 
                        />
                      </div>
                      <p className="profile-chart-note">
                        Apenas tarefas com ferramenta informada
                      </p>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Profile;
